import { GroupLookup } from "src/app/models/lookup.model";
import { ColumnSpan } from "./columnSpan.model";
import { FormFieldType } from "./formFieldType.enum";
import { ILookup } from "./lookup.model";
import { DisplayFormats } from "./displayFormat.constants";
import { Type } from "@angular/core";

export interface IFormField {
    property: string;
    searchProperty?: string;
    name: string;
    required?: boolean;
    readonly?: boolean;
    options?: ILookup[];
    groups?: GroupLookup[];
    items?: FormField[][];
    columnSpan?: ColumnSpan;
    isVisible?: Function;
    min?: number | Date;
    max?: number | Date;
    cssClass?: string;
    iconClass?: string;
    onClick?: Function;
    onChange?: Function;
    displayFormat?: RegExp;
    additionalLabel?: Function;
    searchKeys?: string[];
    searchComponent?: Type<any>;
    additonalSearchData?: Record<string, unknown>;
    optionTemplate?: (option: ILookup, isHeader: boolean) => string;
    optionTemplateFields?: string[];
    optionTemplateLabelMap?: Record<string, string>;
    optionTemplateBadgeFields?: string[];
    selectionTemplate?: (field: FormField) => string;
    onSearchChange?: Function;
    tooltip?: string;
    multiple?: boolean;
    // DASH-2676: opt-in per-field validator. Returns an inline error message when the value is invalid, or
    // null/undefined when valid. When present the message is shown inline (on blur/submit) and the form's Save
    // button is disabled while the field is invalid. Fields that don't set it are unaffected.
    validator?: (value: unknown, entity: unknown) => string | null;
}

export abstract class FormField {
    type: FormFieldType;
    property: string;
    searchProperty: string;
    name: string;
    required: boolean;
    readonly: boolean;
    options: ILookup[];
    groups: GroupLookup[];
    columnSpanClass: string;
    rowSpanClass: string;
    isVisible: Function;
    min: number | Date;
    max: number | Date;
    items: FormField[][];
    cssClass: string;
    iconClass: string;
    onClick: Function;
    onChange: Function;
    displayFormat: RegExp;
    additionalLabel: Function;
    searchKeys: string[];
    searchComponent: Type<any>;
    additonalSearchData: Record<string, unknown>;
    optionTemplate: (option: ILookup, isheader: boolean) => string;
    optionTemplateFields: string[];
    optionTemplateLabelMap: Record<string, string>;
    optionTemplateBadgeFields: string[];
    selectionTemplate: (field: FormField) => string;
    onSearchChange: Function;
    tooltip: string;
    multiple: boolean;
    validator: (value: unknown, entity: unknown) => string | null;

    visible: boolean = true;
    rowVisibility: boolean[] = [];
    additionalLabelText: string = '';
    isRequired: boolean = false;
    itemCssClass: string = '';

    numericMin?: number;
    numericMax?: number;

    constructor(type: FormFieldType, options: IFormField) {
        this.type = type;
        this.searchProperty = options.property + "Search";
        this.property = options.property;
        this.name = options.name;
        this.required = options.required ?? true;
        this.readonly = options.readonly ?? false;
        this.options = options.options ?? [];
        this.groups = options.groups ?? [];
        this.columnSpanClass = (
            options.columnSpan ?? new ColumnSpan(1, 1)
        ).getColumnSpanClass();
        this.isVisible = options.isVisible ?? ((x) => true);
        this.min = options.min;
        this.max = options.max;
        this.numericMin = typeof options.min === 'number' ? options.min : undefined;
        this.numericMax = typeof options.max === 'number' ? options.max : undefined;
        this.items = options.items ? options.items : [[]];
        this.cssClass = options.cssClass ?? "";
        this.iconClass = options.iconClass ?? "";
        this.tooltip = options.tooltip ?? "";
        this.onClick = options.onClick ?? (() => {});
        this.onChange = options.onChange ?? (() => {});
        this.displayFormat = options.displayFormat;
        this.additionalLabel = options.additionalLabel ?? ((x) => "");
        this.searchKeys = options.searchKeys ?? [];
        this.searchComponent = options.searchComponent ?? null;
        this.additonalSearchData = options.additonalSearchData ?? null;
        this.optionTemplate = options.optionTemplate ?? null;
        this.optionTemplateFields = options.optionTemplateFields ?? [];
        this.optionTemplateLabelMap = options.optionTemplateLabelMap ?? {};
        this.optionTemplateBadgeFields =
            options.optionTemplateBadgeFields ?? [];
        this.selectionTemplate = options.selectionTemplate ?? null;
        this.onSearchChange = options.onSearchChange ?? (() => {});
        this.multiple = options.multiple ?? false;
        this.validator = options.validator ?? null;
    }
}

export class TextFormField extends FormField {
    constructor(options: IFormField) {
        super(FormFieldType.Text, options);
    }
}

export class TextAreaFormField extends FormField {
    constructor(options: IFormField) {
        super(FormFieldType.TextArea, options);
    }
}

export class SingleSelectionFormField extends FormField {
    constructor(options: IFormField) {
        super(FormFieldType.SingleSelection, options);
    }
}

export class MultiSelectionFormField extends FormField {
    constructor(options: IFormField) {
        super(FormFieldType.MultipleSelection, options);
    }
}

export class ToggleFormField extends FormField {
    constructor(options: IFormField) {
        super(FormFieldType.Toggle, options);
    }
}

export class CheckboxFormField extends FormField {
    constructor(options: IFormField) {
        super(FormFieldType.Checkbox, options);
    }
}

export class RadioFormField extends FormField {
    constructor(options: IFormField) {
        super(FormFieldType.Radio, options);
    }
}

export class DateFormField extends FormField {
    constructor(options: IFormField) {
        super(FormFieldType.Date, options);
        this.min = options.min as Date;
        this.max = options.max as Date;
    }
}

export class TimeFormField extends FormField {
    constructor(options: IFormField) {
        super(FormFieldType.Time, options);
    }
}

export class DateTimeFormField extends FormField {
    constructor(options: IFormField) {
        super(FormFieldType.DateTime, options);
        this.min = options.min as Date;
        this.max = options.max as Date;
    }
}

export class NumberFormField extends FormField {
    constructor(options: IFormField) {
        super(FormFieldType.Number, options);
        this.min = options.min as number;
        this.max = options.max as number;
        this.displayFormat = options.displayFormat ?? DisplayFormats.Decimal;
    }
}

export class ArrayFormField extends FormField {
    constructor(options: IFormField) {
        super(FormFieldType.Array, options);
    }
}

export class ButtonFormField extends FormField {
    constructor(options: IFormField) {
        super(FormFieldType.Button, options);
    }
}

export class LabelFormField extends FormField {
    constructor(options: IFormField) {
        super(FormFieldType.Label, options);
    }
}

export class FileAttachmentsField extends FormField {
    constructor(options: IFormField) {
        super(FormFieldType.FileAttachments, options);
    }
}

export class HierarchicalSelectionFormField extends FormField {
    constructor(options: IFormField) {
        super(FormFieldType.HierarchicalSelection, options);
    }
}
